import { storageService } from "@/services/storage/service";
import type { CustomBackgroundMetadata } from "@/services/storage/types";

/**
 * In-memory cache of resolved object URLs for globally-stored custom background images, keyed by
 * CustomBackgroundMetadata.id. Object URLs are ephemeral (invalid after a page reload/new
 * session), so this gets populated once per app session from storageService's global
 * custom-backgrounds store — see loadCustomBackgroundsIntoRegistry, called once during
 * EditorProvider's boot sequence, before any project can render. scene-builder.ts reads from this
 * synchronously (buildScene isn't async) rather than depending on React/zustand state directly.
 */
const objectUrlsById = new Map<string, string>();
let loadPromise: Promise<CustomBackgroundMetadata[]> | null = null;

export function getCustomBackgroundUrl(id: string): string | undefined {
	return objectUrlsById.get(id);
}

export function loadCustomBackgroundsIntoRegistry(): Promise<CustomBackgroundMetadata[]> {
	if (loadPromise) return loadPromise;

	loadPromise = (async () => {
		const items = await storageService.loadCustomBackgrounds();
		for (const { metadata, file } of items) {
			if (!objectUrlsById.has(metadata.id)) {
				objectUrlsById.set(metadata.id, URL.createObjectURL(file));
			}
		}
		return items.map((item) => item.metadata);
	})();

	return loadPromise;
}

export function registerCustomBackground({
	metadata,
	file,
}: {
	metadata: CustomBackgroundMetadata;
	file: File;
}): void {
	objectUrlsById.set(metadata.id, URL.createObjectURL(file));
}

export function unregisterCustomBackground(id: string): void {
	const url = objectUrlsById.get(id);
	if (url) {
		URL.revokeObjectURL(url);
		objectUrlsById.delete(id);
	}
}
